import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import React, { useRef, useState } from 'react';
import { terminateMethod } from '../api/calls';

interface Props {
  open: boolean;
  setOpen: React.Dispatch<import('react').SetStateAction<boolean>>;
  consentRef: string | undefined;
  onClose?: () => void;
}

const GeneralModal: React.FC<Props> = ({
  open,
  setOpen,
  consentRef,
  onClose
}) => {
  const [pin, setPin] = useState(new Array(6).fill(''));

  const inputRefs = useRef(new Array(6).fill(null));

  const handleChange = (digit: string, index: number) => {
    const newPin = [...pin];
    newPin[index] = digit;
    setPin(newPin);

    if (digit.length === 1 && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleSubmit = async () => {
    const pinString = pin.join('');
    if (!consentRef) return;
    try {
      await terminateMethod(pinString, consentRef);
    } catch (error) {
      console.log(error);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>

        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3
                  className="mb-6 text-lg font-medium leading-6 text-gray-700"
                  id="modal-title"
                >
                  Confirmation
                </h3>
                <p>
                  You are on the verge of terminating a payment method
                  associated with your AyeFace account. To proceed securely,
                  please enter your PIN for confirmation
                </p>
                <div className="mt-4 border-l-4 border-yellow-400 bg-yellow-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon
                        className="h-5 w-5 text-yellow-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <p className="font-medium text-yellow-700 hover:text-yellow-600">
                          Terminating this payment method will also delete your
                          AyeFace account.
                        </p>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <h3
                    className="mb-6 text-lg font-medium text-gray-700"
                    id="modal-title"
                  >
                    Enter Your 6-Digit Pin
                  </h3>
                  <div>
                    {pin.map((_, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="password"
                        maxLength={1}
                        value={pin[index]}
                        onChange={(e) => handleChange(e.target.value, index)}
                        className="m-1 h-8 w-8 rounded border border-gray-300 text-center"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Terminate
            </button>
            <button
              type="button"
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              onClick={() => {
                setOpen(false);
                if (onClose) {
                  onClose();
                }
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralModal;
